import { call, put, takeLatest } from 'redux-saga/effects';
import * as types from '../../../lib/actionTypes.es6';
import * as actions from './actions.js';
import { getRecentPolicies } from '../../../lib/requests.es6';
import { GAloggerException } from '../../../lib/monitoring.es6';
import { unAuthorizedNotification } from '../../../lib/access.es6';

// Safety ceiling for `fetchAll` requests (see RecentPoliciesWrapper's Un
// Confirmed / Need Action tabs and the maker-checker approval dashboards) —
// protects against an unbounded fetch if a partner's Un_Confirmed queue ever
// grows far beyond what one page load should reasonably pull in.
const FETCH_ALL_MAX_SIZE = 5000;

// How many policies each slice of a progressive load asks for. A complex
// policy carries its whole quotation, schedule entries and dependants, so
// these responses are heavy: pulling the entire queue in one request left the
// screen blank for as long as it took to transfer and parse the lot. Slicing
// puts the first rows up after one round trip and fills in behind them.
//
// Sized for the largest thing that streams through here: the OMM desk
// dashboards sweep a whole status bucket client-side, which is 311 policies on
// prod today. The slices are fetched one after another — page N+1 only goes
// out once N returns — so a small slice size turns one tab load into that many
// sequential round trips, repeated on every tab switch and every search. At 20
// that was 16 calls per load; keep it high enough that a real bucket costs a
// couple of requests.
export const FETCH_ALL_CHUNK_SIZE = 250;

// What the very first request asks for: one table page, so rows are on screen
// after a single short round trip rather than after a whole heavy slice.
export const FIRST_SLICE_SIZE = 20;

// Fetch an entire filtered set in slices, publishing each one as it lands.
//
// Used by views that narrow the server's result set client-side (need_action
// tabs, maker-checker stage baskets): the API can filter on neither, so there
// is no server-side page or total for such a view and the table has to hold
// the whole matching set to count and paginate its own rows.
//
// The slices are sequential — each request goes out only once the previous one
// has landed — so the size governs two things at once: how soon the first rows
// appear, and how many round trips the whole set costs. One size cannot be
// good at both, so the first request is small and the rest are full-size.
function* fetchAllProgressively(payload) {
  const { fetchAll, ...base } = payload;

  const firstResponse = yield call(getRecentPolicies, {
    ...base,
    page: 1,
    size: FIRST_SLICE_SIZE
  });
  const firstBody = firstResponse.data || {};
  const firstSlice = firstBody.data || [];
  const total = firstBody.total || 0;
  const ceiling = Math.min(total, FETCH_ALL_MAX_SIZE);

  const seen = {};
  firstSlice.forEach(function(policy) {
    if (policy) {
      seen[policy.guid] = true;
    }
  });

  const hasMore = firstSlice.length > 0 && firstSlice.length < ceiling;

  // Replaces whatever the previous view left in the store.
  yield put(
    actions.getRecentPoliciesSuccess({
      ...firstBody,
      data: firstSlice,
      total,
      page: 1,
      size: FIRST_SLICE_SIZE,
      loadingMore: hasMore
    })
  );

  if (!hasMore) {
    return;
  }

  // The rest in full-size slices, restarting at page 1: page boundaries are a
  // function of size, so the small first page does not sit on one. That costs
  // one page's worth of rows twice, which is why the second pass drops what
  // the first already published rather than appending it again.
  let page = 1;
  let loaded = 0;

  for (;;) {
    const response = yield call(getRecentPolicies, {
      ...base,
      page,
      size: FETCH_ALL_CHUNK_SIZE
    });
    const body = response.data || {};
    const slice = body.data || [];
    loaded += slice.length;

    const fresh = slice.filter(function(policy) {
      if (!policy || seen[policy.guid]) {
        return false;
      }
      seen[policy.guid] = true;
      return true;
    });

    // An empty slice means the server ran out early — stop rather than page
    // forever against a total that will never be reached.
    const more = slice.length > 0 && loaded < ceiling;

    yield put(
      actions.getRecentPoliciesChunk({
        data: fresh,
        total: body.total || total,
        loadingMore: more
      })
    );

    if (!more) {
      return;
    }
    page += 1;
  }
}

// A plain take()-loop processes one request fully (including the several
// round trips of fetchAllProgressively) before picking up the next one, so
// switching tabs while a slow fetchAll is still streaming leaves the screen
// showing the previous tab's results until that unrelated fetch drains.
// takeLatest cancels whatever request is still in flight as soon as a new
// one is dispatched, so a tab switch always wins over a stale fetch.
export function* getRecentPoliciesRequestWatcher() {
  yield takeLatest(types.GET_RECENT_POLICIES_REQUEST, handleGetRecentPoliciesRequest);
}

function* handleGetRecentPoliciesRequest(request) {
  try {
    if (request.payload.fetchAll) {
      yield call(fetchAllProgressively, request.payload);
    } else {
      const response = yield call(getRecentPolicies, request.payload);
      yield put(actions.getRecentPoliciesSuccess(response.data));
    }
  } catch (e) {
    let errorMessage = 'An unexpected error occurred while fetching recent policies';
    if (e.response && e.response.data) {
      errorMessage = e.response.data.message || e.response.data;
    }
    yield put(actions.getRecentPoliciesFailure(errorMessage));

    GAloggerException(e);
    unAuthorizedNotification(e);
  }
}